import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Clock, TrendingDown, EyeOff, UserX,
  Megaphone, FileText, MessageSquare, Database, CalendarCheck, BarChart,
  ArrowRight, CheckCircle2, Stethoscope, Smile,
  Target, Zap, BarChart3, Shield, Users, TrendingUp,
} from "lucide-react";

const problems = [
  { icon: Clock, text: "Leads are not followed up quickly" },
  { icon: TrendingDown, text: "Low booking rates despite ad spend" },
  { icon: EyeOff, text: "No visibility into lead quality" },
  { icon: UserX, text: "Missed opportunities due to manual processes" },
];

const systemSteps = [
  { icon: Megaphone, label: "Ads / Traffic", desc: "Increase high-converting traffic through SEO, Content Strategy, Google & Meta Ads" },
  { icon: FileText, label: "Landing Pages", desc: "Conversion-optimized pages that capture interest and drive action" },
  { icon: MessageSquare, label: "Instant Follow-up", desc: "SMS & WhatsApp responses within seconds of inquiry" },
  { icon: Database, label: "CRM Tracking", desc: "Every lead tracked, scored, and prioritized automatically" },
  { icon: CalendarCheck, label: "Booking System", desc: "Seamless scheduling that converts leads to appointments" },
  { icon: BarChart, label: "Optimization", desc: "We analyze your daily signals and optimize campaigns in real time" },
];

const caseStudies = [
  {
    id: "dental",
    client: "M.D. Eye Care & Laser Centre",
    icon: Stethoscope,
    locations: "2 Locations",
    problem: "Inconsistent lead flow across 2 locations with no structured follow-up system.",
    results: ["3x increase in inbound leads", "75% faster response time", "2x appointment booking rates"],
  },
  {
    id: "smile-dental",
    client: "Smile The Dental Studio",
    icon: Smile,
    problem: "Low online visibility in a competitive local market with leads not converting.",
    results: ["4x monthly appointment bookings", "65% lower cost per acquisition", "80% inquiries booked within 24hrs"],
  },
];

const whyItWorks = [
  { icon: Target, text: "We focus on bookings and revenue—not just leads" },
  { icon: Zap, text: "Speed-to-lead response within seconds" },
  { icon: BarChart3, text: "Full visibility into your funnel performance" },
  { icon: Shield, text: "Systems that scale without adding headcount" },
];

const ServiceBusinessesLanding = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="pt-8 pb-6 px-4 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Performance Marketing & Automation Agency
            </motion.div>
            <motion.h1
              className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              We Help Service Businesses Turn More Leads Into{" "}
              <span className="text-primary">Booked Customers</span>
            </motion.h1>
            <motion.p
              className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Using high-performing ads, conversion-focused funnels, and automated follow-ups — so you stop losing 30–50% of your leads.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Link to="/contact">
                <Button size="lg" className="text-base px-8 py-5 gap-2">
                  Get a Free Funnel Audit <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/how-it-works">
                <Button size="lg" variant="outline" className="text-base px-8 py-5">
                  See How It Works
                </Button>
              </Link>
            </motion.div>
            <motion.div
              className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm text-muted-foreground"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> 120+ Leads Generated Weekly</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> 30–50% Increase in Booking Rate</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> Used by Growing Service Businesses</span>
            </motion.div>
          </div>
        </section>

        {/* Stats Strip */}
        <section className="py-10 bg-foreground">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { icon: Users, value: "120+", label: "Leads / Week" },
                { icon: CalendarCheck, value: "35%", label: "Increase in Bookings" },
                { icon: TrendingUp, value: "2x", label: "ROI Improvement" },
                { icon: Zap, value: "< 60s", label: "Lead Response Time" },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="text-center"
                >
                  <stat.icon className="h-6 w-6 text-primary mx-auto mb-2" />
                  <div className="text-3xl sm:text-4xl font-bold text-primary-foreground mb-1">{stat.value}</div>
                  <p className="text-sm text-primary-foreground/60">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Problems */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Sound Familiar?
              </h2>
              <p className="text-muted-foreground text-lg">
                Most service businesses face the same conversion bottlenecks.
              </p>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {problems.map((p, i) => (
                <motion.div
                  key={i}
                  className="flex items-start gap-4 p-6 rounded-xl bg-destructive/5 border border-destructive/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                    <p.icon className="w-5 h-5 text-destructive" />
                  </div>
                  <p className="text-foreground font-medium pt-1.5">{p.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* System - Vertical */}
        <section className="py-20 px-4 bg-secondary">
          <div className="max-w-3xl mx-auto">
            <motion.div
              className="text-center mb-14"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                See It In Action
              </h2>
              <p className="text-muted-foreground text-lg">
                Here's what the journey looks like for your next customer.
              </p>
            </motion.div>

            <div className="space-y-4">
              {systemSteps.map((step, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-5 p-5 rounded-xl bg-card border border-border"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="w-12 h-12 rounded-full border-2 border-primary/20 bg-primary/5 flex items-center justify-center shrink-0">
                    <step.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">{step.label}</h3>
                    <p className="text-sm text-muted-foreground">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Real Results */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              className="text-3xl md:text-4xl font-bold text-foreground text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Real Results
            </motion.h2>
            <div className="grid md:grid-cols-2 gap-6">
              {caseStudies.map((cs, i) => (
                <Link to={`/case-studies#${cs.id}`} key={i}>
                  <motion.div
                    className="p-6 rounded-xl bg-card border border-border hover:border-primary/30 hover:shadow-lg transition-all cursor-pointer group h-full"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <cs.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                          {cs.client}
                        </h3>
                        {cs.locations && (
                          <p className="text-xs text-muted-foreground">{cs.locations}</p>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{cs.problem}</p>
                    <div className="space-y-2">
                      {cs.results.map((r, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                          <span className="text-sm font-medium text-foreground">{r}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex items-center gap-1 text-sm text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      View full case study <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Why It Works */}
        <section className="py-20 px-4 bg-secondary">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              className="text-3xl md:text-4xl font-bold text-foreground text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Why Our Approach Works
            </motion.h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {whyItWorks.map((item, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-4 p-5 rounded-xl bg-card border border-border"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-foreground font-medium">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 px-4 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Turn More Leads Into Booked Customers?
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8">
              Get a free audit and see exactly where you're losing leads—and how to fix it.
            </p>
            <Link to="/contact">
              <Button size="lg" variant="secondary" className="text-base px-8 py-6 gap-2">
                Get a Free Audit <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServiceBusinessesLanding;
